import React from "react";
import {
  Box,
  Typography,
  Button,
  CssBaseline
} from "@mui/material";
import { useHistory } from "react-router-dom";

const TERMS_WHITE = "#FFFDF8";
const TERMS_GRAY = "#B8B8B8";
const TERMS_MUTED = "#777777";

function TermsPage() {
  const history = useHistory();

  const sectionTitleSx = {
    fontSize: {
      xs: "1.25rem",
      md: "1.5rem"
    },
    fontWeight: 400,
    letterSpacing: "-0.01em",
    color: TERMS_WHITE,
    mb: 2
  };

  const bodySx = {
    fontSize: {
      xs: "0.95rem",
      md: "1rem"
    },
    lineHeight: 1.8,
    fontWeight: 300,
    color: TERMS_GRAY,
    mb: 2.5
  };

  return (
    <>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000",
          color: TERMS_WHITE
        }}
      >
        {/* =====================================================
            HEADER
            ===================================================== */}

        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,

            bgcolor: "rgba(0, 0, 0, 0.94)",
            backdropFilter: "blur(12px)",

            borderBottom: "1px solid #222",

            px: {
              xs: 3,
              md: 6
            },

            py: 2.5,

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          {/* Logo */}

          <Box
            component="button"
            type="button"
            onClick={() => history.push("/")}
            aria-label="Return to Colloquia"
            sx={{
              border: 0,
              background: "transparent",
              padding: 0,
              cursor: "pointer",

              display: "flex",
              alignItems: "center"
            }}
          >
            <Box
              component="img"
              src="/Colloquia_Logo.png"
              alt="Colloquia"
              sx={{
                height: {
                  xs: 55,
                  md: 65
                },

                width: "auto",

                borderRadius: "12px"
              }}
            />
          </Box>

          <Button
            onClick={() => history.push("/")}
            sx={{
              color: TERMS_GRAY,
              fontWeight: 300,
              textTransform: "none",
              fontSize: "0.9rem",

              "&:hover": {
                color: TERMS_WHITE,
                bgcolor: "transparent"
              }
            }}
          >
            Return to Colloquia
          </Button>
        </Box>

        {/* =====================================================
            MAIN CONTENT
            ===================================================== */}

        <Box
          component="main"
          sx={{
            width: "100%",
            maxWidth: 920,

            mx: "auto",

            px: {
              xs: 4,
              sm: 6,
              md: 8
            },

            pt: {
              xs: 8,
              md: 12
            },

            pb: {
              xs: 10,
              md: 14
            }
          }}
        >
          {/* =====================================================
              PAGE INTRO
              ===================================================== */}

          <Box
            sx={{
              mb: {
                xs: 7,
                md: 9
              }
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "2.5rem",
                  sm: "3.2rem",
                  md: "4rem"
                },

                lineHeight: 1.1,
                fontWeight: 300,
                letterSpacing: "-0.035em",

                color: TERMS_WHITE,

                mb: 2
              }}
            >
              Terms of Service
            </Typography>

            <Typography
              sx={{
                color: TERMS_MUTED,
                fontSize: "0.85rem",
                fontWeight: 300
              }}
            >
              Last updated: August 24, 2026
            </Typography>
          </Box>

          {/* =====================================================
              INTRODUCTION
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={bodySx}>
              These Terms of Service ("Terms") govern your access
              to and use of Colloquia, including the Colloquia
              website, applications, and related services
              (collectively, the "Service").
            </Typography>

            <Typography sx={bodySx}>
              By accessing or using the Service, you agree to be
              bound by these Terms. If you do not agree to these
              Terms, you may not access or use the Service.
            </Typography>
          </Box>

          {/* =====================================================
              ABOUT COLLOQUIA
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              About Colloquia
            </Typography>

            <Typography sx={bodySx}>
              Colloquia is an online social media platform focused
              on intellectual discourse, discussion, and the
              exchange of ideas.
            </Typography>

            <Typography sx={bodySx}>
              References to "Colloquia," "we," "us," or "our" in
              these Terms refer to the current operator of the
              Colloquia Service. The Service may subsequently be
              reorganized, transferred, or operated through a
              legal entity established for that purpose.
            </Typography>
          </Box>

          {/* =====================================================
              ELIGIBILITY
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Eligibility
            </Typography>

            <Typography sx={bodySx}>
              You must be legally capable of entering into these
              Terms in your jurisdiction to use the Service.
            </Typography>

            <Typography sx={bodySx}>
              By creating an account or using the Service, you
              represent that you meet the applicable eligibility
              requirements and that the information you provide
              is accurate to the best of your knowledge.
            </Typography>
          </Box>

          {/* =====================================================
              ACCOUNTS
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Accounts
            </Typography>

            <Typography sx={bodySx}>
              Certain features of the Service require you to
              create an account. You are responsible for maintaining
              the confidentiality of your account credentials and
              for activity occurring through your account.
            </Typography>

            <Typography sx={bodySx}>
              You agree to provide accurate information when
              creating your account and to keep that information
              reasonably current.
            </Typography>

            <Typography sx={bodySx}>
              You should notify us if you believe that your account
              credentials have been compromised or that someone has
              accessed your account without authorization.
            </Typography>
          </Box>

          {/* =====================================================
              ACCEPTABLE USE
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Acceptable Use
            </Typography>

            <Typography sx={bodySx}>
              You agree not to use the Service to violate
              applicable law, infringe the rights of others,
              interfere with the operation or security of the
              Service, or engage in fraudulent, abusive, or
              otherwise harmful conduct.
            </Typography>

            <Typography sx={bodySx}>
              You may not attempt to gain unauthorized access to
              accounts, systems, networks, or data associated with
              the Service.
            </Typography>

            <Typography sx={bodySx}>
              You may not use automated means to access or interact
              with the Service in a manner that could disrupt,
              overload, or impair the Service or interfere with
              other users' access to it.
            </Typography>
          </Box>

          {/* =====================================================
              USER CONTENT
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              User Content
            </Typography>

            <Typography sx={bodySx}>
              You retain ownership of content that you submit,
              post, or otherwise make available through the
              Service, subject to the rights and licenses necessary
              for Colloquia to operate, maintain, display,
              distribute, and improve the Service.
            </Typography>

            <Typography sx={bodySx}>
              You are responsible for the content you submit and
              represent that you have the necessary rights and
              permissions to submit that content.
            </Typography>

            <Typography sx={bodySx}>
              By submitting content to the Service, you grant the
              current operator of Colloquia a non-exclusive,
              worldwide, royalty-free license to host, store,
              reproduce, display, distribute, and technically
              process that content as reasonably necessary to
              provide and operate the Service.
            </Typography>
          </Box>

          {/* =====================================================
              INTELLECTUAL PROPERTY
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Intellectual Property
            </Typography>

            <Typography sx={bodySx}>
              The Service, including its software, design, branding,
              logos, graphics, interfaces, and other original
              materials, is owned by or licensed to the current
              operator of Colloquia and is protected by applicable
              intellectual-property laws.
            </Typography>

            <Typography sx={bodySx}>
              Except as expressly permitted by these Terms, you may
              not copy, modify, distribute, sell, lease, reverse
              engineer, or otherwise exploit substantial portions
              of the Service without appropriate authorization.
            </Typography>
          </Box>

          {/* =====================================================
              THIRD-PARTY SERVICES
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Third-Party Services
            </Typography>

            <Typography sx={bodySx}>
              The Service may rely on or integrate with third-party
              services, technologies, or providers. Third-party
              services may be subject to their own terms and
              privacy policies.
            </Typography>

            <Typography sx={bodySx}>
              We are not responsible for the availability,
              functionality, or policies of third-party services
              that are outside our control.
            </Typography>
          </Box>

          {/* =====================================================
              TERMINATION
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Suspension and Termination
            </Typography>

            <Typography sx={bodySx}>
              We may suspend or terminate accounts or access to the
              Service where we reasonably believe that a user has
              violated these Terms, applicable law, or otherwise
              misused the Service.
            </Typography>

            <Typography sx={bodySx}>
              You may stop using the Service at any time.
            </Typography>
          </Box>

          {/* =====================================================
              DISCLAIMERS
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Disclaimers
            </Typography>

            <Typography sx={bodySx}>
              The Service is provided on an "as is" and "as
              available" basis to the extent permitted by
              applicable law.
            </Typography>

            <Typography sx={bodySx}>
              We do not guarantee that the Service will always be
              available, uninterrupted, secure, accurate, or
              error-free, or that particular content or features
              will remain available indefinitely.
            </Typography>
          </Box>

          {/* =====================================================
              LIMITATION OF LIABILITY
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Limitation of Liability
            </Typography>

            <Typography sx={bodySx}>
              To the maximum extent permitted by applicable law,
              the current operator of Colloquia will not be liable
              for indirect, incidental, special, consequential, or
              punitive damages arising from or relating to your use
              of, or inability to use, the Service.
            </Typography>

            <Typography sx={bodySx}>
              Nothing in these Terms is intended to exclude or limit
              liability that cannot legally be excluded or limited
              under applicable law.
            </Typography>
          </Box>

          {/* =====================================================
              CHANGES
              ===================================================== */}

          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              Changes to These Terms
            </Typography>

            <Typography sx={bodySx}>
              We may update these Terms from time to time as the
              Service develops or as circumstances change.
            </Typography>

            <Typography sx={bodySx}>
              When we make material changes, we may provide notice
              through the Service or by other reasonable means.
              The updated Terms will become effective on the date
              specified in the revised Terms.
            </Typography>

            <Typography sx={bodySx}>
              Your continued use of the Service after the effective
              date of updated Terms constitutes acceptance of the
              revised Terms, to the extent permitted by applicable
              law.
            </Typography>
          </Box>

          {/* =====================================================
              CONTACT
              ===================================================== */}

          <Box sx={{ mb: 10 }}>
            <Typography sx={sectionTitleSx}>
              Contact
            </Typography>

            <Typography sx={bodySx}>
              If you have questions about these Terms, please
              contact us through the contact information provided
              on the Colloquia Contact page.
            </Typography>

            <Button
              variant="outlined"
              onClick={() => history.push("/contact")}
              sx={{
                mt: 1,

                color: TERMS_WHITE,
                borderColor: "#444",

                textTransform: "none",
                fontWeight: 300,

                "&:hover": {
                  borderColor: "#777",
                  bgcolor: "#111"
                }
              }}
            >
              Contact Colloquia
            </Button>
          </Box>

          {/* =====================================================
              FOOTER NAVIGATION
              ===================================================== */}

          <Box
            sx={{
              borderTop: "1px solid #222",

              pt: 4,

              display: "flex",
              flexWrap: "wrap",

              gap: {
                xs: 0.5,
                md: 1
              }
            }}
          >
            {[
              ["About", "/about"],
              ["Terms", "/terms"],
              ["Privacy", "/privacy"],
              ["Careers", "/careers"],
              ["Support", "/support"],
              ["Contact", "/contact"]
            ].map(([label, path]) => (
              <Button
                key={label}
                onClick={() => history.push(path)}
                sx={{
                  color:
                    label === "Terms"
                      ? TERMS_WHITE
                      : TERMS_MUTED,

                  textTransform: "none",
                  fontWeight: 300,
                  fontSize: "0.9rem",

                  minWidth: "auto",
                  px: 1,

                  "&:hover": {
                    color: TERMS_WHITE,
                    bgcolor: "transparent"
                  }
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Typography
            sx={{
              mt: 3,
              color: "#555",
              fontSize: "0.8rem",
              fontWeight: 300
            }}
          >
            © 2026 Colloquia from Penneferies Squire Capital
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default TermsPage;